import React from "react";
import { Search, Gamepad2, Check } from "lucide-react";
import ImageUpload from "./ImageUpload";
import { FORMATS, T_STATUSES, TIMEZONES, inputCls as inp } from "./constants";

export const Fld = ({ label, children, hint }) => (
  <div>
    <label className="block text-xs font-semibold text-white/50 mb-1.5">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-white/30 mt-1">{hint}</p>}
  </div>
);

const Toggle = ({ checked, onChange, label, testid }) => (
  <label className="flex items-center justify-between py-1.5 cursor-pointer">
    <span className="text-sm text-white/80">{label}</span>
    <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} data-testid={testid}
      className={`w-10 h-6 rounded-full p-0.5 transition-colors ${checked ? "bg-[hsl(var(--primary))]" : "bg-white/15"}`}>
      <span className={`block w-5 h-5 rounded-full bg-white transition-transform ${checked ? "translate-x-4" : ""}`} />
    </button>
  </label>
);

export function StepGame({ f, upd, games }) {
  const [q, setQ] = React.useState("");
  const list = games.filter((g) => g.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <h3 className="text-lg font-bold text-white">Choose Your Game</h3>
      <p className="text-sm text-white/50 mb-4">Select the game for your tournament.</p>
      <div className="relative mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for a game…" className={`${inp} pl-9`} data-testid="wiz-game-search" />
      </div>
      <div className="grid sm:grid-cols-2 gap-2 max-h-[360px] overflow-y-auto pr-1" data-testid="wiz-game-list">
        {list.map((g) => {
          const on = f.game === g.name;
          return (
            <button type="button" key={g.id || g.name} onClick={() => upd({ game: g.name })} data-testid={`wiz-game-${g.name}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-colors ${on ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10" : "border-white/10 hover:border-white/25 hover:bg-white/[0.03]"}`}>
              <Gamepad2 size={16} className={on ? "text-[hsl(var(--primary))]" : "text-white/40"} />
              <span className="flex-1 text-sm font-semibold text-white">{g.name}</span>
              {on && <Check size={16} className="text-[hsl(var(--primary))]" />}
            </button>
          );
        })}
        {!list.length && <div className="text-sm text-white/40 py-6">No games found.</div>}
      </div>
    </div>
  );
}

export function StepDetails({ f, upd }) {
  return (
    <div className="space-y-4">
      <Fld label="Tournament Name *"><input value={f.name} onChange={(e) => upd({ name: e.target.value })} data-testid="wiz-name" className={inp} placeholder="e.g. 7 Legendary Community Cup" maxLength={120} /></Fld>
      <Fld label={`Description (${f.description.length}/5000)`}><textarea value={f.description} onChange={(e) => upd({ description: e.target.value })} rows={3} maxLength={5000} className={inp} data-testid="wiz-description" /></Fld>
      <Fld label="Tournament Cover / Overview Image"><ImageUpload value={f.cover} onChange={(cover) => upd({ cover })} testid="wiz-cover-upload" label="Upload tournament image" /></Fld>
      <div className="grid sm:grid-cols-2 gap-4">
        <Fld label="Start Date & Time"><input type="datetime-local" value={f.start_date} onChange={(e) => upd({ start_date: e.target.value })} className={inp} data-testid="wiz-start" /></Fld>
        <Fld label="End Date & Time"><input type="datetime-local" value={f.end_date} onChange={(e) => upd({ end_date: e.target.value })} className={inp} data-testid="wiz-end" /></Fld>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Fld label="Timezone"><select value={f.timezone} onChange={(e) => upd({ timezone: e.target.value })} className={inp} data-testid="wiz-timezone">{[...new Set([f.timezone, ...TIMEZONES])].map((z) => <option key={z}>{z}</option>)}</select></Fld>
        <Fld label="Prize Pool"><input value={f.prize_pool} onChange={(e) => upd({ prize_pool: e.target.value })} className={inp} placeholder="$500 / Community Rewards" data-testid="wiz-prize" /></Fld>
        <Fld label="Location"><input value={f.location} onChange={(e) => upd({ location: e.target.value })} className={inp} data-testid="wiz-location" /></Fld>
      </div>
    </div>
  );
}

export function StepRegistration({ f, upd }) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/10 p-4 text-sm text-white/80">
        <b className="text-white">Open Registration.</b> After the tournament is created you get a public registration link and QR code. Teams sign up with their name, logo, members, country and description, and appear automatically in your team list.
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Fld label="Max Teams"><input type="number" min={2} max={256} value={f.max_teams} onChange={(e) => upd({ max_teams: Number(e.target.value) })} className={inp} data-testid="wiz-max-teams" /></Fld>
        <Fld label="Players per Team"><input type="number" min={1} max={20} value={f.team_size} onChange={(e) => upd({ team_size: Number(e.target.value) })} className={inp} data-testid="wiz-team-size" /></Fld>
      </div>
      <Toggle checked={f.registration_open} onChange={(v) => upd({ registration_open: v })} label="Registration open" testid="wiz-reg-open" />
      <Fld label="Terms & Conditions" hint="Shown on the tournament page and the registration form.">
        <textarea value={f.terms} onChange={(e) => upd({ terms: e.target.value })} rows={6} maxLength={20000} className={inp} placeholder={"1. All players must…\n2. Matches start on time…"} data-testid="wiz-terms" />
      </Fld>
    </div>
  );
}

export function StepSettings({ f, upd, showStatus = true }) {
  return (
    <div className="space-y-4">
      <Fld label="Tournament Format">
        <div className="grid grid-cols-2 gap-3">
          {FORMATS.map((fm) => (
            <button type="button" key={fm.v} onClick={() => upd({ format: fm.v })} data-testid={`wiz-format-${fm.v}`}
              className={`text-left p-3 rounded-lg border transition-colors ${f.format === fm.v ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10" : "border-white/10 hover:border-white/25"}`}>
              <div className="text-sm font-bold text-white">{fm.label}</div>
              <div className="text-xs text-white/50">{fm.desc}</div>
            </button>
          ))}
        </div>
      </Fld>
      {f.format === "groups" && (
        <div className="grid grid-cols-2 gap-4">
          <Fld label="Teams per Group"><input type="number" min={2} max={16} value={f.group_size} onChange={(e) => upd({ group_size: Number(e.target.value) })} className={inp} data-testid="wiz-group-size" /></Fld>
          <Fld label="Advance per Group"><input type="number" min={1} max={8} value={f.advance_per_group} onChange={(e) => upd({ advance_per_group: Number(e.target.value) })} className={inp} data-testid="wiz-advance" /></Fld>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <Fld label="Match Format"><select value={f.match_format} onChange={(e) => upd({ match_format: e.target.value })} className={inp} data-testid="wiz-match-format">{["Best of 1", "Best of 3", "Best of 5"].map((x) => <option key={x}>{x}</option>)}</select></Fld>
        <Fld label="Grand Final"><select value={f.grand_final_format} onChange={(e) => upd({ grand_final_format: e.target.value })} className={inp} data-testid="wiz-gf-format">{["Best of 1", "Best of 3", "Best of 5", "Best of 7"].map((x) => <option key={x}>{x}</option>)}</select></Fld>
      </div>
      <Toggle checked={f.third_place} onChange={(v) => upd({ third_place: v })} label="Third Place Match" testid="wiz-third-place" />
      <Toggle checked={f.bracket_public} onChange={(v) => upd({ bracket_public: v })} label="Show Bracket to Public" testid="wiz-bracket-public" />
      {showStatus && (
        <Fld label="Status">
          <select value={f.status} onChange={(e) => upd({ status: e.target.value })} className={inp} data-testid="wiz-status">{T_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}</select>
        </Fld>
      )}
    </div>
  );
}
